/**
 * Vuex module for persisting Analytics dashboard preferences per community.
 *
 * Mirrors the per-community localStorage pattern used by the `filters` module.
 * Tracking-id selections are intentionally NOT stored here — they are owned by
 * the `filters` module (SSoT) and reused across the app.
 *
 * Persisted per community:
 *  - preset            : range preset IDENTITY (e.g. "last_7_days"); Custom keeps dates
 *  - customFrom/customTo : absolute ISO datetimes, only meaningful when preset === 'custom'
 *  - granularity       : hour|day|week|month
 *  - splitByTrackingId : boolean toggle for the revenue/orders chart
 *  - splitChartType    : 'line' | 'bar' — how the split-by-tracking-id view renders
 *  - collapsedSections : map of sectionId -> boolean (true = collapsed)
 */

import { PRESET_IDS, GRANULARITIES } from '@/utils/dateRangePresets'

const STORAGE_PREFIX = 'analytics_prefs_'

function defaultPrefs() {
  return {
    preset: PRESET_IDS.LAST_7_DAYS,
    customFrom: null,
    customTo: null,
    granularity: GRANULARITIES.DAY,
    splitByTrackingId: false,
    splitChartType: 'line',
    showAllSeries: false,
    cumulative: false,
    distView: 'dow',
    collapsedSections: {}
  }
}

export const state = {
  // Current, live preferences for the active community.
  current: defaultPrefs()
}

export const getters = {
  currentPrefs: (state) => state.current,
  preset: (state) => state.current.preset,
  granularity: (state) => state.current.granularity,
  splitByTrackingId: (state) => state.current.splitByTrackingId,
  splitChartType: (state) => ['bar', 'bar_grouped'].includes(state.current.splitChartType) ? state.current.splitChartType : 'line',
  showAllSeries: (state) => Boolean(state.current.showAllSeries),
  cumulative: (state) => Boolean(state.current.cumulative),
  distView: (state) => state.current.distView === 'heatmap' ? 'heatmap' : 'dow',
  customRange: (state) => ({ from: state.current.customFrom, to: state.current.customTo }),
  isSectionCollapsed: (state) => (sectionId) => Boolean(state.current.collapsedSections[sectionId])
}

export const mutations = {
  SET_PREFS(state, prefs) {
    state.current = { ...defaultPrefs(), ...prefs }
  },
  SET_PRESET(state, preset) {
    state.current.preset = preset
  },
  SET_CUSTOM_RANGE(state, { from, to }) {
    state.current.customFrom = from
    state.current.customTo = to
  },
  SET_GRANULARITY(state, granularity) {
    state.current.granularity = granularity
  },
  SET_SPLIT_BY_TRACKING_ID(state, value) {
    state.current.splitByTrackingId = Boolean(value)
  },
  SET_SPLIT_CHART_TYPE(state, value) {
    state.current.splitChartType = ['bar', 'bar_grouped'].includes(value) ? value : 'line'
  },
  SET_SHOW_ALL_SERIES(state, value) {
    state.current.showAllSeries = Boolean(value)
  },
  SET_CUMULATIVE(state, value) {
    state.current.cumulative = Boolean(value)
  },
  SET_DIST_VIEW(state, value) {
    state.current.distView = value === 'heatmap' ? 'heatmap' : 'dow'
  },
  SET_SECTION_COLLAPSED(state, { sectionId, collapsed }) {
    state.current.collapsedSections = {
      ...state.current.collapsedSections,
      [sectionId]: Boolean(collapsed)
    }
  }
}

export const actions = {
  loadForCommunity({ commit }, communityId) {
    if (!communityId) {
      commit('SET_PREFS', defaultPrefs())
      return
    }
    try {
      const stored = localStorage.getItem(`${STORAGE_PREFIX}${communityId}`)
      if (stored) {
        commit('SET_PREFS', JSON.parse(stored))
      } else {
        commit('SET_PREFS', defaultPrefs())
      }
    } catch (error) {
      console.error('Error loading analytics prefs from localStorage:', error)
      commit('SET_PREFS', defaultPrefs())
    }
  },

  saveForCommunity({ state }, communityId) {
    if (!communityId) return
    try {
      localStorage.setItem(`${STORAGE_PREFIX}${communityId}`, JSON.stringify(state.current))
    } catch (error) {
      console.error('Error saving analytics prefs to localStorage:', error)
    }
  },

  setPreset({ commit, dispatch, rootGetters }, preset) {
    commit('SET_PRESET', preset)
    dispatch('saveForCommunity', rootGetters['community/currentCommunityId'])
  },

  setCustomRange({ commit, dispatch, rootGetters }, range) {
    commit('SET_CUSTOM_RANGE', range)
    dispatch('saveForCommunity', rootGetters['community/currentCommunityId'])
  },

  setGranularity({ commit, dispatch, rootGetters }, granularity) {
    commit('SET_GRANULARITY', granularity)
    dispatch('saveForCommunity', rootGetters['community/currentCommunityId'])
  },

  setSplitByTrackingId({ commit, dispatch, rootGetters }, value) {
    commit('SET_SPLIT_BY_TRACKING_ID', value)
    dispatch('saveForCommunity', rootGetters['community/currentCommunityId'])
  },

  setSplitChartType({ commit, dispatch, rootGetters }, value) {
    commit('SET_SPLIT_CHART_TYPE', value)
    dispatch('saveForCommunity', rootGetters['community/currentCommunityId'])
  },

  setShowAllSeries({ commit, dispatch, rootGetters }, value) {
    commit('SET_SHOW_ALL_SERIES', value)
    dispatch('saveForCommunity', rootGetters['community/currentCommunityId'])
  },

  setCumulative({ commit, dispatch, rootGetters }, value) {
    commit('SET_CUMULATIVE', value)
    dispatch('saveForCommunity', rootGetters['community/currentCommunityId'])
  },

  setDistView({ commit, dispatch, rootGetters }, value) {
    commit('SET_DIST_VIEW', value)
    dispatch('saveForCommunity', rootGetters['community/currentCommunityId'])
  },

  setSectionCollapsed({ commit, dispatch, rootGetters }, payload) {
    commit('SET_SECTION_COLLAPSED', payload)
    dispatch('saveForCommunity', rootGetters['community/currentCommunityId'])
  }
}
